import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import {
  useGoals,
  usePlans,
  useActivities,
  useCreateGoal,
} from "@/hooks/useCareer";
import { Modal } from "@/components/ui/modal";
import {
  EntityRow,
  FilterChips,
  ProgressBar,
  StatusChip,
  fieldClassName,
} from "@/components/goals/goals-ui";
import { isOpenWork } from "@/lib/goals-progress";

type GoalFilter = "active" | "paused" | "done" | "all";

export function GoalsListPage() {
  const navigate = useNavigate();
  const goalsQuery = useGoals();
  const plansQuery = usePlans();
  const activitiesQuery = useActivities(false);
  const createGoal = useCreateGoal();

  const [filter, setFilter] = useState<GoalFilter>("active");
  const [createOpen, setCreateOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [success, setSuccess] = useState("");
  const [goalType, setGoalType] = useState("role");

  const plans = plansQuery.data?.plans ?? [];
  const activities = activitiesQuery.data?.activities ?? [];
  const goals = goalsQuery.data?.goals ?? [];

  const filtered = useMemo(() => {
    return goals.filter((g) => {
      if (filter === "all") return true;
      if (filter === "active") return g.status === "active";
      if (filter === "paused") return g.status === "paused";
      return g.status === "completed" || g.status === "abandoned";
    });
  }, [goals, filter]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !success.trim()) return;
    const { goal } = await createGoal.mutateAsync({
      title: title.trim(),
      successDefinition: success.trim(),
      goalType,
    });
    setCreateOpen(false);
    setTitle("");
    setSuccess("");
    navigate(`/goals/${goal.id}`);
  }

  return (
    <div className="pb-8">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <FilterChips
          value={filter}
          onChange={setFilter}
          options={[
            { value: "active", label: "Active" },
            { value: "paused", label: "Paused" },
            { value: "done", label: "Done" },
            { value: "all", label: "All" },
          ]}
        />
        <div className="flex gap-2">
          <Link
            to="/ai-assistant?prompt=Draft+a+career+goal+for+me+based+on+my+profile"
            className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs text-muted-foreground hover:text-foreground"
          >
            <Sparkles className="size-3.5" />
            Ask AI
          </Link>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground"
          >
            New goal
          </button>
        </div>
      </div>

      {goalsQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading goals…</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border px-4 py-10 text-center">
          <p className="text-sm text-muted-foreground">No goals in this filter.</p>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="mt-3 text-sm text-brand hover:underline"
          >
            Create your first goal
          </button>
        </div>
      ) : (
        <ul className="space-y-2">
          {filtered.map((g) => {
            const planCount = plans.filter((p) => p.goalId === g.id).length;
            const openWork = activities.filter(
              (a) => a.goalId === g.id && isOpenWork(a.state),
            ).length;
            const pct = g.progressPercent ?? 0;
            return (
              <EntityRow
                key={g.id}
                to={`/goals/${g.id}`}
                title={g.title}
                meta={
                  <span className="flex flex-wrap items-center gap-2">
                    <StatusChip>{g.goalType}</StatusChip>
                    <span>{g.status}</span>
                    {g.targetDate ? (
                      <span>target {g.targetDate.slice(0, 10)}</span>
                    ) : null}
                    <span>
                      {planCount} plan{planCount === 1 ? "" : "s"} · {openWork}{" "}
                      open work
                    </span>
                  </span>
                }
                trailing={
                  <div className="w-16 shrink-0 text-right">
                    <p className="text-xs text-muted-foreground">{pct}%</p>
                    <ProgressBar value={pct} className="mt-1" />
                  </div>
                }
              />
            );
          })}
        </ul>
      )}

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} labelledBy="new-goal-title">
        <form onSubmit={(e) => void handleCreate(e)} className="flex flex-col">
          <div className="border-b border-border px-4 py-3">
            <h2 id="new-goal-title" className="text-base font-semibold text-foreground">
              New goal
            </h2>
          </div>
          <div className="space-y-3 overflow-y-auto px-4 py-4">
            <input
              className={fieldClassName()}
              placeholder="Goal title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
              required
            />
            <textarea
              className={fieldClassName()}
              placeholder="What does success look like?"
              rows={3}
              value={success}
              onChange={(e) => setSuccess(e.target.value)}
              required
            />
            <select
              className={fieldClassName()}
              value={goalType}
              onChange={(e) => setGoalType(e.target.value)}
            >
              <option value="role">Role</option>
              <option value="skill">Skill</option>
              <option value="network">Network</option>
              <option value="offer">Offer</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          <div className="flex justify-end gap-2 border-t border-border px-4 py-3">
            <button
              type="button"
              onClick={() => setCreateOpen(false)}
              className="rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createGoal.isPending}
              className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground disabled:opacity-50"
            >
              Create
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
